import { debounce, isNil, pickBy } from 'lodash';
import { Order } from 'types';
import { computed, onMounted, ref, UnwrapRef, watch } from 'vue';
import { LocationQuery, useRoute, useRouter } from 'vue-router';
import { SELECT_ALL } from '../constants';
import { Filtering, PageOptions, TableMode } from '../TableWrapper.types';
import { namespaceQueryParams as namespaceQueryParamsUtil } from '../utils';
import { isoDateRegex } from 'utils';
import { stringToBoolean } from '@/helpers';

export function useTableWrapper<T extends object, U extends object = T>(
  mode: TableMode,
  initialSortBy: keyof T | undefined = undefined,
  fetchItemsFunction?: () => Promise<void>,
  initialSortOrder: Order = Order.ASC,
  useQueryParams: boolean = true,
  queryParamsNamespace: string = '',
) {
  const isInitializing = ref(true);
  const filters = ref<Filtering<U>>({});
  const pageOptions = ref<PageOptions<T>>({
    page: 1,
    perPage: 10,
    rows: 0,
    sortBy: initialSortBy,
    sortOrder: initialSortOrder,
  });
  const selection = ref<T[]>([]);
  const filterParams = computed(() => {
    return pickBy(
      filters.value,
      (value) => !isNil(value) && value !== '' && value !== SELECT_ALL,
    ) as Filtering<U>;
  });
  const isLoading = ref(false);

  async function fetch(isDebounce = false): Promise<void> {
    if (!fetchItemsFunction) return;
    isLoading.value = true;
    if (!isDebounce) {
      await fetchItemsFunction();
    } else {
      await debounce(fetchItemsFunction, 200)();
    }
    isLoading.value = false;
  }

  const route = useRoute();
  const router = useRouter();

  function preprocessQueryParams(query: LocationQuery): LocationQuery {
    return Object.fromEntries(
      Object.entries(query).map(([key, value]) => {
        if (value instanceof Date) {
          return [key, value.toISOString()];
        } else return [key, value];
      }),
    );
  }

  function namespaceQueryParams(query: LocationQuery, namespace?: string) {
    return namespaceQueryParamsUtil(query, namespace ?? queryParamsNamespace);
  }
  function unnamespaceQueryParams(query: LocationQuery) {
    if (queryParamsNamespace) {
      const unnamespacedQuery: LocationQuery = {};
      Object.keys(query).forEach((key) => {
        if (key.startsWith(`${queryParamsNamespace}_`)) {
          unnamespacedQuery[key.replace(`${queryParamsNamespace}_`, '')] =
            query[key];
        }
      });
      return unnamespacedQuery;
    } else return query;
  }

  function updateRouteQuery() {
    const query: Record<string, string> = {
      page: pageOptions.value.page.toString(),
      perPage: pageOptions.value.perPage.toString(),
      ...filterParams.value,
    };
    if (pageOptions.value.sortBy) {
      query.sortBy = String(pageOptions.value.sortBy);
    }
    if (pageOptions.value.sortOrder) {
      query.sortOrder = pageOptions.value.sortOrder;
    }
    router.replace({
      params: { ...route.params },
      query: preprocessQueryParams(namespaceQueryParams(query)),
    });
  }

  function initializeFromQuery() {
    const q = unnamespaceQueryParams(route.query);
    if (q.page) pageOptions.value.page = parseInt(q.page as string);
    if (q.perPage) pageOptions.value.perPage = parseInt(q.perPage as string);
    if (q.sortBy) pageOptions.value.sortBy = q.sortBy as UnwrapRef<keyof T>;
    if (q.sortOrder) pageOptions.value.sortOrder = q.sortOrder as Order;

    const newFilters: Partial<Filtering<U>> = {};
    Object.keys(q).forEach((key) => {
      if (!['page', 'perPage', 'sortBy', 'sortOrder'].includes(key)) {
        let value = q[key] as Extract<
          U[keyof U],
          string | number | boolean | Date
        >;
        if (typeof value === 'string' && value.match(isoDateRegex)) {
          value = new Date(value) as any;
        }
        if (value === 'true' || value === 'false') {
          value = stringToBoolean(value) as any;
        }
        newFilters[key as keyof U] = value;
      }
    });
    filters.value = newFilters as Filtering<U>;
  }

  watch(
    pageOptions,
    () => {
      if (isInitializing.value) return;
      if (mode === TableMode.LAZY) fetch(false);
      if (useQueryParams) updateRouteQuery();
    },
    { deep: true },
  );
  // TODO: Don't use debounce on selects
  watch(
    filterParams,
    () => {
      if (isInitializing.value) return;
      if (mode === TableMode.LAZY) fetch(true);
      if (useQueryParams) updateRouteQuery();
    },
    { deep: true },
  );

  onMounted(async () => {
    if (useQueryParams) initializeFromQuery();
    await fetch(false);
    isInitializing.value = false;
  });

  return {
    pageOptions,
    filterParams,
    isLoading,
    filters,
    selection,
    fetch,
    namespaceQueryParams,
  };
}
